// src/lib/constants.ts
import type { PaymentType, FAQ } from '@/types/calculator';

export const PAYMENT_TYPES: PaymentType[] = [
  {
    id: 'goods',
    label: 'Waren oder Dienstleistungen bezahlen',
    rate: 0.0249,
    fixed: 0.35,
    description: 'Standardgebühr für Waren und Dienstleistungen',
    example: {
      amount: 100,
      fees: 2.84,
      total: 102.84,
      description: 'Bei einem Warenwert von 100€ fallen 2,84€ Gebühren an (2,49% + 0,35€)'
    }
  },
  {
    id: 'friends',
    label: 'Zahlung an Freunde und Familie',
    rate: 0,
    fixed: 0,
    description: 'Kostenlos innerhalb Deutschlands',
    example: {
      amount: 50,
      fees: 0,
      total: 50,
      description: 'Bei einer Überweisung von 50€ an Familie fallen keine Gebühren an'
    }
  },
  {
    id: 'donations',
    label: 'Spenden sammeln',
    rate: 0.0249,
    fixed: 0.35,
    description: 'Gebühren für Spendensammlung',
    example: {
      amount: 200,
      fees: 5.33,
      total: 205.33,
      description: 'Bei einer Spende von 200€ fallen 5,33€ Gebühren an (2,49% + 0,35€)'
    }
  },
  {
    id: 'micro',
    label: 'Mikrozahlung',
    rate: 0.05,
    fixed: 0.05,
    description: 'Für Kleinstbeträge unter 10€',
    example: {
      amount: 5,
      fees: 0.30,
      total: 5.30,
      description: 'Bei einer Mikrozahlung von 5€ fallen 0,30€ Gebühren an (5% + 0,05€)'
    }
  },
  {
    id: 'merchant-1',
    label: 'Händlerkonditionen < 2.000 €',
    rate: 0.0229,
    fixed: 0.35,
    description: 'Vergünstigte Konditionen bis 2.000€ Umsatz',
    example: {
      amount: 1000,
      fees: 23.25,
      total: 1023.25,
      description: 'Bei 1.000€ Umsatz fallen 23,25€ Gebühren an (2,29% + 0,35€)'
    }
  },
  {
    id: 'merchant-2',
    label: 'Händlerkonditionen 2.000 - 5.000 €',
    rate: 0.0219,
    fixed: 0.35,
    description: 'Vergünstigte Konditionen für mittleren Umsatz',
    example: {
      amount: 3000,
      fees: 66.05,
      total: 3066.05,
      description: 'Bei 3.000€ Umsatz fallen 66,05€ Gebühren an (2,19% + 0,35€)'
    }
  },
  {
    id: 'merchant-3',
    label: 'Händlerkonditionen 5.000 - 25.000 €',
    rate: 0.0199,
    fixed: 0.35,
    description: 'Vergünstigte Konditionen für hohen Umsatz',
    example: {
      amount: 10000,
      fees: 199.35,
      total: 10199.35,
      description: 'Bei 10.000€ Umsatz fallen 199,35€ Gebühren an (1,99% + 0,35€)'
    }
  },
  {
    id: 'merchant-4',
    label: 'Händlerkonditionen > 25.000 €',
    rate: 0.0179,
    fixed: 0.35,
    description: 'Beste Konditionen für sehr hohen Umsatz',
    example: {
      amount: 30000,
      fees: 537.35,
      total: 30537.35,
      description: 'Bei 30.000€ Umsatz fallen 537,35€ Gebühren an (1,79% + 0,35€)'
    }
  },
  {
    id: 'qr',
    label: 'Zahlung mit QR-Code',
    rate: 0.005,
    fixed: 0.10,
    description: 'Spezielle Konditionen für QR-Code Zahlungen',
    example: {
      amount: 20,
      fees: 0.20,
      total: 20.20,
      description: 'Bei einer QR-Code Zahlung von 20€ fallen 0,20€ Gebühren an (0,5% + 0,10€)'
    }
  }
];

export const EXAMPLES = PAYMENT_TYPES.map(type => type.example);

export const FAQ_ITEMS: FAQ[] = [
  {
    question: 'Welche Gebühren fallen bei PayPal für Verkäufer an?',
    answer: 'Als Verkäufer zahlen Sie bei Standard-Transaktionen 2,49% plus 0,35€ pro Zahlung. Bei höherem Umsatz können Sie von günstigeren Händlerkonditionen profitieren, die bis auf 1,79% plus 0,35€ sinken können.',
    category: 'basic'
  },
  {
    question: 'Wie berechnen sich die Gebühren bei internationalen Zahlungen?',
    answer: 'Bei internationalen Zahlungen kommt zu den Standardgebühren ein Aufschlag hinzu: Innerhalb der EU +0,5%, für Großbritannien +1,29%, für USA/Kanada +1,99% und für alle anderen Länder +2,99%.',
    category: 'international'
  },
  {
    question: 'Wie funktionieren die PayPal Händlerkonditionen?',
    answer: 'Die Händlerkonditionen staffeln sich nach Umsatz: Bis 2.000€ (2,49% + 0,35€), 2.000-5.000€ (2,19% + 0,35€), 5.000-25.000€ (1,99% + 0,35€), über 25.000€ (1,79% + 0,35€).',
    category: 'business'
  },
  {
    question: 'Wie funktioniert die QR-Code Zahlung bei PayPal?',
    answer: 'Bei QR-Code Zahlungen über 10,01€ beträgt die Gebühr 0,5% + 0,10€. Für Beträge bis 10€ wird 1% + 0,05€ berechnet.',
    category: 'basic'
  },
  {
    question: 'Was sind die aktuellen Änderungen der PayPal Gebühren 2025?',
    answer: 'Seit Januar 2025 beträgt die Standardgebühr 2,49% + 0,35€. Für QR-Code-Zahlungen gelten vergünstigte Konditionen, und die Händlerkonditionen wurden angepasst.',
    category: 'basic'
  }
];

export const EXAMPLE_CATEGORIES = {
  standard: {
    title: 'Standard Zahlungen',
    description: 'Beispiele für alltägliche PayPal-Transaktionen'
  },
  business: {
    title: 'Händlerkonditionen',
    description: 'Beispiele für verschiedene Umsatzstufen'
  },
  special: {
    title: 'Spezielle Zahlungen',
    description: 'Beispiele für QR-Code und Mikrozahlungen'
  }
};

// Helper function for currency formatting
export const formatCurrency = (amount: number): string => {
  return new Intl.NumberFormat('de-DE', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(amount);
};

// Helper function for percentage formatting
export const formatPercentage = (rate: number): string => {
  return new Intl.NumberFormat('de-DE', {
    style: 'percent',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(rate);
};

// Helper function for fee calculation
export const calculateFees = (amount: number, rate: number, fixed: number): number => {
  return parseFloat((amount * rate + fixed).toFixed(2));
};